import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ShoppingCartOutlined } from '@ant-design/icons'
import { Collapse, Tabs, Button, message, Popover, Row, Col } from 'antd'
import * as moment from 'moment'
import Assign from './assign.component'
import ConfirmModal from './confirm-modal.component'
import { standardDate } from '../../flag-management.tools'
import { resetDeliveryTries } from '../../flag-management.services'
import ResourceSteps from '../../../../Main/Components/ResourceSteps'
import ResourceProfile from './../../../resource-profile'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import { useParams, useHistory, useLocation } from 'react-router-dom'
import { TaskActionsFooter } from '../../../../refactory/modules/jobs/infrastructure/job-detail/tasks/task-actions-footer.component'
import { StepTypeNames, TaskNames } from '../../../../refactory/modules/jobs/domain/task'
import { JobAssignedToRouteBanner } from '../../../../refactory/modules/jobs/infrastructure/job-detail/tasks/job-assigned-to-route-banner-component'
import { useGate } from 'statsig-react'
import { Can } from '../../../../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import { SubModulePerms } from '../../../../refactory/modules/access-control/domain/module'
import EmptyState from '../../../../refactory/kernel/infrastructure/empty-state.component'
import { AbbreviatedText } from '../../../../shared/abbreviated-text'
import { filterTaskWithoutMultistopCanceled } from '../../../../refactory/utils/transform'
import { getStores } from '../../../resource-management/resource-management.services'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(localizedFormat)

const { TabPane } = Tabs
const { Panel } = Collapse

const RESET = 'RESET'
const DONE = 'DONE'

const TASKS_ORDER = {
  ZONE_PICKING: 0,
  CONSOLIDATION: 1,
  PICKING: 1,
  FULL_SERVICE: 1,
  PICKING_MULTI_STOP: 2,
  PICKING_VERIFICATION: 3,
  DELIVERY: 4,
  PICKING_WITH_STORAGE: 5,
  STORAGE: 6,
  PICKING_AND_STORAGE: 7,
  PICK_UP: 8,
  PICK_UP_FOR_DELIVERY: 9,
  DELIVERY_WITH_STORAGE: 10
}

const toTimestamp = (date) => new Date(date).getTime()

const Task = (props) => {
  const isCreateRoutingV2Active = useGate('is_create_routing_v2_active').value
  const location = useLocation()
  const { resourceIdDetail } = useParams()
  const history = useHistory()
  const { t } = useTranslation()
  const [showAssign, setShowAssign] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [selectedTask, setSelectedTask] = useState()
  const [isReAssign, setIsReAssign] = useState(false)
  const [actualTabIndex, setActualTabIndex] = useState(0)
  const [hoverUnlock] = useState(false)
  const [popoverConfirm, setPopoverConfirm] = useState({ unlock: false })
  const [confirmType, setConfirmType] = useState(RESET)
  const [childSteps, setChildSteps] = useState([])
  const [childResource, setChildResource] = useState({})
  const [showResourceProfile, setShowResourceProfile] = useState(false)
  const [selectedResourceId, setSelectedResourceId] = useState()
  const [tasks, setTasks] = useState([])
  const [storesTasksMultistop, setStoresTasksMultistop] = useState([])
  const [taskStore, setTaskStore] = useState(props.store)
  const operationModel = props.operationModel
  const unlock = async () => {
    const { jobId, setSelectedJob } = props
    const updatedJob = await resetDeliveryTries(jobId)
    if (updatedJob) {
      message.success(t('jobs.tasks.reset-tries.success'))
      setSelectedJob(updatedJob)
      handlePopover('unlock')
    }
  }

  useEffect(() => {
    if (selectedTask?.name === TaskNames.PICKING_MULTI_STOP && storesTasksMultistop?.length) {
      let storeMultiStop = storesTasksMultistop.find(({ id }) => id === selectedTask.storeId)
      if (storeMultiStop) setTaskStore(() => storeMultiStop)
      return
    }

    setTaskStore(() => props.store)
  }, [selectedTask, storesTasksMultistop])

  useEffect(() => {
    if (tasks?.length) {
      getStoresDetailTaskPickingMultistop()
    }
  }, [tasks])

  useEffect(() => {
    const tasksFound = props.job.tasks.find((task) => task.id === selectedTask?.id)
    if (tasksFound) {
      setSelectedTask(tasksFound)
    }

    setTasks(filterTaskWithoutMultistopCanceled(props.job.tasks))
  }, [props.job])

  const getStoresDetailTaskPickingMultistop = async () => {
    const multiStopTaskStoreIds = tasks
      .filter(({ name, storeId }) => name === TaskNames.PICKING_MULTI_STOP && storeId)
      .map((task) => task.storeId)

    if (multiStopTaskStoreIds.length) {
      try {
        const { stores } = await getStores({
          limit: multiStopTaskStoreIds.length,
          offset: 0,
          stores: multiStopTaskStoreIds
        })

        setStoresTasksMultistop(stores?.length ? stores.map(({ id, location }) => ({ id, ...location })) : [])
      } catch (_) {
        setStoresTasksMultistop([])
      }
    }
  }

  const handleReAssignModal = (reAssign) => {
    if (!location.pathname.includes(`re-assign`)) {
      history.push(`${location.pathname}/re-assign`)
    } else if (location.pathname.includes('re-assign') && showAssign) {
      history.replace(location.pathname.split('/re-assign')[0])
    }
    setIsReAssign(reAssign)
    setShowAssign(!showAssign)
  }

  const onCloseConfirm = () => {
    setShowConfirm(!showConfirm)
  }

  const handleConfirmModal = (type) => {
    onCloseConfirm()
    setConfirmType(type)
  }

  const isTransferred = (steps) => {
    const result = steps.find((step) => {
      return (
        (step.type.name === StepTypeNames.TRANSFERRING_TO ||
          step.type.name === StepTypeNames.TRANSFERRING_TO_DELIVERY_FROM_STORAGE) &&
        step.status === DONE
      )
    })
    if (result) return true
    return false
  }

  const getOrderTasks = (tasks) => {
    return tasks.sort((a, b) => {
      if (a.name === 'PICKING_MULTI_STOP' && b.name === 'PICKING_MULTI_STOP') {
        const stopOrderA = a.stopMetadata?.order
        const stopOrderB = b.stopMetadata?.order
        return stopOrderA - stopOrderB
      } else {
        if (a.name === TaskNames.ZONE_PICKING && b.name === TaskNames.ZONE_PICKING) {
          const optimalStartTaskA = a.optimalStartTime ? toTimestamp(standardDate(a.optimalStartTime)) : 0
          const optimalStartTaskB = b.optimalStartTime ? toTimestamp(standardDate(b.optimalStartTime)) : 0

          if (optimalStartTaskA === optimalStartTaskB) {
            const taskNameA = getTabNameNotMultiStop(a.name, a.departments ? a.departments[0] : undefined)
            const taskNameB = getTabNameNotMultiStop(b.name, b.departments ? b.departments[0] : undefined)

            return taskNameA.localeCompare(taskNameB)
          }

          return optimalStartTaskA - optimalStartTaskB
        }

        const taskOrderA = TASKS_ORDER[a.name]
        const taskOrderB = TASKS_ORDER[b.name]
        return taskOrderA - taskOrderB
      }
    })
  }

  const getOrderSteps = (steps) => {
    return steps.sort((a, b) => {
      if (a.ordering && b.ordering) {
        // Todo: Remove this when all steps using ordering
        return a.ordering - b.ordering
      } else {
        return toTimestamp(standardDate(a.optimalStart)) - toTimestamp(standardDate(b.optimalStart))
      }
    })
  }

  const assignBtn = {
    name: t('jobs.tasks.assign-btn'),
    action: () => handleReAssignModal(false),
    disabled: props.isCancelled || props.isDelivered()
  }

  const reAssignBtn = (steps) => {
    return {
      name: t('jobs.tasks.re-assign-btn'),
      action: () => handleReAssignModal(true),
      disabled: props.isCancelled || props.isDelivered() || isTransferred(steps)
    }
  }

  const locale = {
    optimal: t('jobs.tasks.task.optimum'),
    start: t('jobs.tasks.changeOptimalStart.start'),
    end: t('jobs.tasks.changeOptimalStart.end'),
    optimalStart: t('jobs.tasks.changeOptimalStart.optimalStart'),
    optimalEnd: t('jobs.tasks.changeOptimalStart.optimalEnd'),
    unassigned: t('jobs.tasks.task.unassigned'),
    phone: t('jobs.tasks.task.phone'),
    onHold: t('jobs.tasks.changeOptimalStart.onHold'),
    inProgress: t('jobs.tasks.changeOptimalStart.inProgress')
  }

  const getTabNameNotMultiStop = (name, department) => {
    let tabName = t(`jobs.tasks.name.${name}`)
    if (name === 'ZONE_PICKING' && department) {
      tabName = t(`jobs.tasks.name.ZONE`) + ` - ${department.toLowerCase()}`
    }

    return tabName
  }

  const getTabName = (name, department, stopMetadata) => {
    let tabName = getTabNameNotMultiStop(name, department)

    if (name === TaskNames.PICKING_MULTI_STOP && stopMetadata) {
      tabName = (
        <>
          {`${t('abbreviation-stop')}${stopMetadata.order || ''} - `}
          <AbbreviatedText text={stopMetadata.storeName || ''} maxLength={10} />
        </>
      )
    }
    return tabName
  }

  const getTapPanes = (tasks) => {
    const { forceDelivery } = props
    const orderTasks = getOrderTasks(tasks)
    return orderTasks.map((task, index) => {
      const { name, payment, confirmPayment, steps, departments, stopMetadata } = task
      const firstDepartment = departments ? departments[0] : undefined
      let stringDepartments = ''

      if (departments && departments.length > 0) {
        stringDepartments = departments.join(' | ')
      }

      return (
        <TabPane tab={getTabName(name, firstDepartment, stopMetadata)} key={index}>
          {name === 'ZONE_PICKING' ? (
            <label style={{ fontSize: '14', color: '#2C2E30', paddingLeft: '30' }}>
              {t(`jobs.tasks.name.DEPARTMENTS`)}:{' '}
              <span style={{ fontSize: '14', color: '#868B90', textTransform: 'lowercase' }}>{stringDepartments} </span>
            </label>
          ) : null}

          {isCreateRoutingV2Active && selectedTask && selectedTask.route && (
            <JobAssignedToRouteBanner
              route={selectedTask.route}
              store={props.store}
              onCloseModal={props.onCancel}
              selectedTask={selectedTask}
            />
          )}

          {selectedTask && (
            <Row>
              <Col span={15}>
                <ResourceSteps
                  payment={payment}
                  selectedTask={selectedTask}
                  confirmPayment={confirmPayment}
                  resourceInfo={childResource}
                  steps={childSteps}
                  locale={locale}
                  assignBtn={assignBtn}
                  reAssignBtn={reAssignBtn(steps)}
                  showButton={Can([SubModulePerms.JOB_TASK_REASIGN])}
                  onClickResource={onClickResource}
                  job={props.job}
                  jobsSearch={props.jobsSearch}
                  setSelectedJob={props.setSelectedJob}
                  timezone={props.tz}
                />
              </Col>
            </Row>
          )}
          <TaskActionsFooter
            task={selectedTask}
            tasks={tasks}
            forceDelivery={forceDelivery}
            operationalModel={operationModel}
            isCancelled={props.isCancelled}
            isDelivered={() => props.isDelivered()}
            handleConfirmModal={handleConfirmModal}
          />
        </TabPane>
      )
    })
  }

  const changeTap = (key) => {
    setActualTabIndex(key)
    setSelectedTask(tasks[key])
  }

  const handlePopover = (popoverRef) => {
    setPopoverConfirm({
      [popoverRef]: !popoverConfirm[popoverRef]
    })
  }

  const getConfirmOptions = (popoverRef, action, task) => {
    return (
      <div className='confirm-options'>
        <Button onClick={() => handlePopover(popoverRef)} size='small'>
          <u>{t('jobs.job.info.items-view.no')}</u>
        </Button>
        <Button id='reset-delivery-tries' onClick={() => action(task)} type='primary' size='small'>
          <u>{t('jobs.job.info.items-view.yes')}</u>
        </Button>
      </div>
    )
  }

  const actions = (task) => {
    if (!task || !Can([SubModulePerms.JOB_TASK_UNLOCK])) return false
    const hoverUnLockClass = hoverUnlock ? 'active-hover' : ''
    return (
      <>
        <div className='task-header'>
          <div>
            {needsResetDeliveryTries(task) ? (
              <Popover
                id='popover-confirm'
                content={getConfirmOptions('unlock', unlock)}
                title={t('jobs.job.info.items-view.confirm')}
                trigger='click'
                visible={popoverConfirm.unlock}
                onVisibleChange={() => handlePopover('unlock')}
              >
                <Button
                  className={hoverUnLockClass}
                  style={{ marginRight: 8 }}
                  disabled={props.isCancelled || props.isDelivered()}
                >
                  {t('jobs.tasks.reset-delivery-tries')}
                </Button>
              </Popover>
            ) : null}
          </div>
        </div>
      </>
    )
  }

  const needsResetDeliveryTries = (task) => {
    const isDeliveryTask = task.steps.filter((step) => step.type.name === StepTypeNames.DELIVERING).length > 0
    const { deliveryCodeTries, deliveryCodeTriesLimit } = props
    return isDeliveryTask && deliveryCodeTries >= deliveryCodeTriesLimit
  }

  const handleShowResourceProfile = (visible, resourceId) => {
    if (!location.pathname.includes(`resources`)) {
      history.push(`${location.pathname}/resources/${resourceId}`)
    } else if (location.pathname.includes('resources') && showResourceProfile) {
      history.replace(location.pathname.split('/resources')[0])
    }
    setShowResourceProfile(visible)
  }

  useEffect(() => {
    if (location.pathname.includes('re-assign')) {
      setIsReAssign(true)
      setShowAssign(true)
    }

    if (resourceIdDetail && location.pathname.endsWith(`/resources/${resourceIdDetail}`)) {
      setSelectedResourceId(resourceIdDetail)
      setShowResourceProfile(true)
    }
    if (tasks) {
      setSelectedTask(tasks[actualTabIndex])
    }
  }, [actualTabIndex, tasks])

  useEffect(() => {
    if (!props.job?.id) return
    handleInitilizeTabs()
  }, [props.job?.id])

  const handleInitilizeTabs = useCallback(() => {
    if (!props.job?.tasks || props.job?.tasks.length === 0) return
    setActualTabIndex(0)
  }, [props.job?.tasks])

  useEffect(
    (_) => {
      const dateFormatStep = (date) => {
        if (standardDate(date)) {
          const dateToMoment = moment(standardDate(date)).tz(props.tz)
          return `${dateToMoment.format('LT')}, ${dateToMoment.format('D MMM')}`
        }
        return ''
      }

      if (selectedTask) {
        let { steps, resources } = selectedTask
        if (selectedTask.name === TaskNames.DELIVERY) {
          steps = steps.filter(({ active }) => active !== false)
        }

        const sortedSteps = getOrderSteps(steps)
        setChildSteps(
          sortedSteps.map((step) => ({
            steps,
            actualEnd: dateFormatStep(step.actualEnd),
            actualStart: dateFormatStep(step.actualStart),
            optimalStart: dateFormatStep(step.optimalStart),
            optimalEnd: dateFormatStep(step.optimalFinish),
            status: step.status,
            uuid: step.uuid,
            name: t(`jobs.tasks.type.${step.type.name}`),
            type: step.type,
            active: step.active
          }))
        )

        setChildResource(
          resources
            ? {
                src: resources.photoUrl,
                name: resources.firstName,
                phone: resources.phoneNumber,
                resourceId: resources.resourceId
              }
            : {}
        )
      }
    },
    [props.tz, selectedTask, t]
  )

  const onClickResource = (resourceId) => {
    if (resourceId) {
      setSelectedResourceId(resourceId)
      handleShowResourceProfile(true, resourceId)
    }
  }

  const ResourceProfileParams = {
    ...props,
    resourceId: selectedResourceId,
    visible: showResourceProfile,
    onChangeVisible: handleShowResourceProfile,
    clientTimeZone: props.tz
  }

  return (
    <Collapse className='tasks-card' bordered={false} defaultActiveKey={['1']} expandIconPosition='right'>
      <Panel
        header={
          <div>
            <ShoppingCartOutlined className='shopping-cart-blue' />
            <b>{` ${t('jobs.tasks.title')}`}</b>
          </div>
        }
        key='1'
      >
        {(() => {
          if (!tasks) {
            return <EmptyState subtitle={t('empty.no-data')} />
          } else {
            return (
              <>
                <Tabs tabBarExtraContent={actions(selectedTask)} defaultActiveKey='0' onChange={changeTap}>
                  {getTapPanes(tasks)}
                </Tabs>
              </>
            )
          }
        })()}
        {showAssign && (
          <Assign
            {...props}
            isReAssign={isReAssign}
            setSelectedJob={props.setSelectedJob}
            onClose={handleReAssignModal}
            jobId={props.jobId}
            task={selectedTask || null}
            store={taskStore}
            visible={showAssign}
            jobsSearch={props.jobsSearch}
            tz={props.tz}
          />
        )}
        {showConfirm && (
          <ConfirmModal
            type={confirmType}
            setSelectedJob={props.setSelectedJob}
            setJob={props.setJob}
            jobId={props.jobId}
            task={selectedTask}
            taskId={selectedTask ? selectedTask.uuid : null}
            taskName={selectedTask ? selectedTask.name : null}
            onClose={onCloseConfirm}
            visible={showConfirm}
            jobsSearch={props.jobsSearch}
          />
        )}
        {ResourceProfileParams.visible && <ResourceProfile {...ResourceProfileParams} />}
      </Panel>
    </Collapse>
  )
}

export default Task
